import React, { useEffect, useState } from "react";
import "./CreateAd.scss";
import { Breadcrumb, Button, Steps } from "antd";
import Helpers from "@utils/Helpers";
import { errorLog } from "logging";
import { AdApi } from "services/AdApi";
import ChooseProduct from "./ChooseProduct/ChooseProduct";
import SetAd from "./SetAd/SetAd";
import Preview from "./Preview/Preview";
import { ProductApi } from "services/ProductApi";
import dayjs from "dayjs";
import { useUnit } from "contexts/UnitContext";
import { useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const CreateAd: React.FC = () => {
  const navigate = useNavigate();
  const [currStep, setCurrStep] = useState(0);
  const { convertCurrency, units } = useUnit();
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [allProducts, setAllProducts] = useState<any>(null);
  const [adType, setAdType] = useState<any>(null);
  const [adData, setAdData] = useState<any>({
    start_time: dayjs(),
    duration: 1,
    discounted_percent: 10,
    discounted_price: 0,
    discounted_unit: "Ton",
    discounted_quantity: 1,
    product_ad_type_id: 1,
    product_id: 0,
  })

  useEffect(() => {
    const getAdType = async () => {
      try {
        const response = await AdApi.getType();
        if (response.status === 200) {
          setAdType(response.data.ad_types);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch data. Please try again");
        errorLog("SetAd.tsx - getAdType", error);
      }
    };

    getAdType();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await ProductApi.GetMyProducts();
        if (response.status === 200) {
          const data = response.data.data;
          setAllProducts(data);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch data. Please try again");
        errorLog("ProductCatalog.tsx - fetchProducts", error);
      }
    };

    if (!allProducts) {
      fetchProducts();
    }
  }, [allProducts]);

  const handleSetAd = async () => {
    let finalDiscountedPrice = 0;
    if (adData.discounted_unit === "tons") {
      const pricePerKg = adData.discounted_price / 1000;
      finalDiscountedPrice = convertCurrency(pricePerKg, units.currency, "USD");
    } else {
      finalDiscountedPrice = convertCurrency(
        adData.discounted_price / selectedProduct.bale_weight,
        units.currency,
        "USD"
      );
    }

    const body = {
      ...adData,
      duration: adData.duration * 7,
      discounted_price: finalDiscountedPrice,
      product_id: selectedProduct.product_id,
    };
    try {
      const response = await AdApi.createAd(body);
      if (response.status === 201) {
        Helpers.showSuccessUpdated("Create ad successfully");
        navigate("/profile?direct=ads");
      }
    } catch (error) {
      Helpers.notification.error("Can not create ad. Please try again");
      errorLog("Preview.tsx - handleSetAd", error);
    }
  };

  const handleValidateAdForm = () => {
    if (
      adData.product_ad_type_id === 0 ||
      adData.discounted_percent === 0 ||
      adData.discounted_price === 0 ||
      adData.discounted_quantity === 0 ||
      adData.discounted_quantity === null ||
      adData.start_time === "" ||
      adData.ad_end_time === ""
    ) {
      Helpers.notification.warning("Please fill in all the fields");
      return;
    }
    if (adData.discounted_percent % 1 !== 0) {
      Helpers.notification.warning("Discount percentage must be an integer");
      return;
    }
    if (adData.discounted_quantity > selectedProduct.quantity) {
      Helpers.notification.warning(
        "Discounted quantity must be less than or equal to stock"
      );
      return;
    }
    if (adData.discounted_percent > 100 || adData.discounted_percent < 0) {
      Helpers.notification.warning(
        "Discount percentage must be between 0 and 100"
      );
      return;
    }
    setCurrStep(2);
  };

  return (
    <div className="CreateAd">
      <Breadcrumb
        className="title"
        separator=">"
        items={[
          {
            title: "All Ads",
            href: "/profile?direct=ads",
          },
          {
            title: "Create New Ad",
          },
        ]}
      />
      <Steps
        size="small"
        current={currStep}
        items={[
          {
            title: "Choose Product",
          },
          {
            title: "Set Ads",
          },
          {
            title: "Preview & Publish",
          },
        ]}
      />
      {currStep === 0 && (
        <ChooseProduct
          adData={adData}
          setAdData={setAdData}
          setSelectedProduct={setSelectedProduct}
        />
      )}

      {currStep === 1 && (
        <SetAd
          adData={adData}
          setAdData={setAdData}
          selectedProduct={selectedProduct}
          adType={adType}
        />
      )}

      {currStep === 2 && (
        <Preview
          adData={adData}
          allProducts={allProducts}
          selectedProduct={selectedProduct}
        />
      )}
      <div className="control-btn">
        {currStep > 0 && (
          <Button
            className="back-btn"
            size="large"
            onClick={() => setCurrStep(currStep - 1)}
          >
            <LeftOutlined /> Back
          </Button>
        )}
        <Button
          className="next-btn"
          size="large"
          disabled={currStep === 0 ? (!selectedProduct ? true : false) : false}
          onClick={() =>
            currStep < 2
              ? currStep === 1
                ? handleValidateAdForm()
                : setCurrStep(currStep + 1)
              : handleSetAd()
          }
        >
          {currStep < 2 ? "Next" : "Publish"} <RightOutlined />
        </Button>
      </div>
    </div>
  );
};

export default CreateAd;
